export interface UploadedImage {
  url: string;
  alt: string;
}

/**
 * Posts one image to the admin upload endpoint and returns what the body
 * should link to. It lives outside the component because jsdom cannot drive
 * ProseMirror, so this is the only place the request contract -- the CSRF
 * header, the refusal path -- can be tested without a browser.
 */
export async function uploadImage(
  file: File,
  uploadUrl: string,
): Promise<UploadedImage> {
  const form = new FormData();
  form.append("file", file);
  const token = document.querySelector<HTMLMetaElement>(
    'meta[name="csrf-token"]',
  )?.content;

  const response = await fetch(uploadUrl, {
    method: "POST",
    body: form,
    headers: token ? { "X-CSRF-Token": token } : {},
  });
  const body = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(body.errors ?? "That image could not be uploaded.");
  }

  // A 2xx whose body did not parse lands here with nothing in it, and an
  // unchecked read would insert ![undefined](undefined) and call it a success.
  if (typeof body.url !== "string") {
    throw new Error("That image could not be uploaded.");
  }

  return { url: body.url, alt: typeof body.alt === "string" ? body.alt : "" };
}

export function imageFilesFrom(files: FileList | null | undefined): File[] {
  return Array.from(files ?? []).filter((file) =>
    file.type.startsWith("image/"),
  );
}
